// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — remises au guichet d'une campagne en distribution. Parcours en quatre étapes :
//   1. l'agent contrôle la carte QR du salarié (« Contrôler la carte » ouvre l'écran de contrôle, qui reprend le numéro du
//      contrôle VALIDE ; à défaut — contrôle fait avec un autre outil — numéro saisi, ou dernier contrôle VALIDE de l'agent
//      pour ce salarié retenu par le serveur) ; la carte contrôlée doit être celle du salarié choisi ;
//   2. choix de l'attribution et de la quantité (remise partielle possible) ;
//   3. pièce d'émargement (bon de remise signé) ;
//   4. confirmation (Idempotency-Key) → remise numérotée, sortie de stock, créance et échéancier (FINANCE), montants à
//      retenir proposés à la Paie et BLOQUÉS (simulation, aucune transmission).
// Un agent ne se remet jamais rien à lui-même (refus du serveur). Routes : POST/GET /:id/remises.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, PackageCheck, QrCode, RotateCcw, ScanQrCode, Search } from 'lucide-react';
import { Bouton, BandeauInfo, Squelette } from '../../design/composants';
import { toArray, libelle } from './api';
import { Champ, ErreurServeur } from './commun';
import { useSocial, nouvelleCle, cleARenouveler, francs, dateHeureLongue } from './apiSocial';
import { useLectureSociale, EtatsLecture, TitreSection, Infos, Montant, BandeauRetenuesSimulation, usePieceDepot, ChampPiece, erreurSociale } from './communSocial';
import { Echeanciers, RetenuesProposees, BadgeSimulation } from './EcheanciersCommun';
import { useCampagnes, quantiteUnites, ETATS_INSCRIPTION } from './apiCampagnes';
import { codeEtat, lireEntier } from './communCampagnes';
import { FenetreControleCarte } from './carte/ControleCarte';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Fraîcheur du contrôle de carte exigée par le serveur (constante de sécurité, rappelée pour l'agent). */
const FRAICHEUR_MIN = 15;

function Etape({ n, titre, fait, children }) {
  return (
    <li style={{ listStyle: 'none', display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: 10, padding: '12px 0', borderTop: n > 1 ? '1px solid var(--sg-bord)' : 0 }}>
      <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600,
        background: fait ? 'var(--sg-primaire)' : 'var(--sg-fond)', color: fait ? '#fff' : 'var(--sg-texte-2)', border: '1px solid var(--sg-bord-fort)' }}>
        {fait ? <CheckCircle2 size={15} /> : n}
      </span>
      <div style={{ minWidth: 0 }}>
        <h3 style={{ margin: '2px 0 8px', fontSize: 15, fontWeight: 600 }}>Étape {n} — {titre}</h3>
        {children}
      </div>
    </li>
  );
}

/** Inscriptions qui ont encore des unités attribuées à remettre (attribuées, ou remises en partie). */
function useARemettre(campagneId) {
  const api = useCampagnes();
  return useLectureSociale(async () => {
    const [a, b] = await Promise.all([
      api.inscriptions(campagneId, { page: 1, taille: 200, statut: 'ATTRIBUEE' }),
      api.inscriptions(campagneId, { page: 1, taille: 200, statut: 'PARTIELLEMENT_REMISE' }),
    ]);
    return [...toArray(a), ...toArray(b)];
  }, [api, campagneId]);
}

const reste = (i) => Number(i.quantite_attribuee || 0) - Number(i.quantite_remise || 0);

/** Résultat d'une remise : créance et échéancier constitués par FINANCE, retenues proposées et bloquées. */
function ResultatRemise({ campagne, resultat, onNouvelle }) {
  const social = useSocial();
  const r = resultat;
  const cr = useLectureSociale(() => (r.creance?.id ? social.creance(r.creance.id) : Promise.resolve(null)), [social, r.creance?.id]);
  // Montant et rang des retenues : lus s'ils sont renvoyés (DM-WEB-003 n° 5), sinon l'échéancier affiché au-dessus fait foi.
  const retenues = toArray(r.retenues).map((x) => ({
    cle_idempotence: x.cle || x.cle_idempotence, periode: x.periode, statut: x.statut, verdict: x.verdict, motifs_blocage: x.motifs || x.motifs_blocage,
    montant_demande: x.montant ?? x.montant_demande, rang: x.rang, total: x.total,
  }));
  return (
    <section className="rh-carte" aria-live="polite">
      <TitreSection action={<Bouton petit Icone={RotateCcw} onClick={onNouvelle}>Nouvelle remise</Bouton>}>Remise {r.remise?.numero} enregistrée</TitreSection>
      <Infos lignes={[
        ['Inscription', r.inscription ? `${r.inscription.numero} — ${libelle(ETATS_INSCRIPTION, r.inscription.statut)}` : null],
        ['Quantité remise', quantiteUnites(campagne, r.remise?.quantite)], ['Montant brut', <Montant n={r.remise?.montant_brut} />],
        ['Aide imputée', <Montant n={r.remise?.aide_imputee} />], ['Montant à rembourser constitué', <Montant n={r.remise?.montant_constitue} fort />],
        ['Écriture (démonstration)', r.ecriture?.numero || null],
      ]} />
      {!r.creance && <BandeauInfo ton="info" titre="Rien à rembourser">Le reste à charge est nul : ni créance, ni échéancier, ni retenue.</BandeauInfo>}
      {r.creance && (
        <>
          <p style={{ margin: '12px 0 8px', fontSize: 14 }}>
            Créance constituée{r.echeancier ? ` : échéancier version ${r.echeancier.version}, ${francs(r.echeancier.montant_total)} en ${r.echeancier.nb_echeances} mois` : ''}.{' '}
            <Link className="rh-lien" to={`/dispositifs/${campagne.organisation_id}/tresorerie/creances/${r.creance.id}`}>Ouvrir la créance</Link>{' '}
            <BadgeSimulation actif={campagne.simulation || cr.donnees?.simulation} />
          </p>
          {cr.erreur && <ErreurServeur erreur={cr.erreur} />}
          {!cr.donnees && !cr.erreur && <Squelette largeur="60%" />}
          {cr.donnees && <Echeanciers versions={cr.donnees.echeanciers} />}
          <div style={{ marginTop: 12 }}><RetenuesProposees retenues={retenues} /></div>
        </>
      )}
    </section>
  );
}

function RemiseGuichet({ campagne, onFaite }) {
  const api = useCampagnes();
  const liste = useARemettre(campagne.id);
  const [carteOk, setCarteOk] = useState(false);
  const [controle, setControle] = useState('');
  const [carte, setCarte] = useState(null);
  const [fenetreCarte, setFenetreCarte] = useState(false);
  const [autreOutil, setAutreOutil] = useState(false);
  const [filtre, setFiltre] = useState('');
  const [choix, setChoix] = useState(null);
  const [quantite, setQuantite] = useState('');
  const [boucles, setBoucles] = useState('');
  const [cle, setCle] = useState(nouvelleCle);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState(null);
  const piece = usePieceDepot(campagne.organisation_id, 'INSCRIPTION', choix?.id || null, 'BON_REMISE');
  const candidats = toArray(liste.donnees).filter((i) => reste(i) > 0);
  const t = filtre.trim().toLowerCase();
  const visibles = t ? candidats.filter((i) => `${i.salarie} ${i.matricule} ${i.numero}`.toLowerCase().includes(t)) : candidats;
  const controleOk = !controle.trim() || UUID_RE.test(controle.trim());
  const q = lireEntier(quantite);
  const qOk = choix && q && q >= 1 && q <= reste(choix);
  const listeBoucles = boucles.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
  const bouclesOk = listeBoucles.length <= (q || 0);
  const etape1 = carte ? true : (carteOk && controleOk);
  const matriculeCarte = carte?.identite?.matricule || null;
  const carteAutre = !!(matriculeCarte && choix && choix.matricule && String(choix.matricule) !== String(matriculeCarte));
  const etape2 = etape1 && qOk && bouclesOk && !carteAutre;
  const ageCarteMin = carte ? (Date.now() - new Date(carte.le).getTime()) / 60000 : 0;
  const nomCarte = carte?.identite ? [carte.identite.prenom, carte.identite.nom].filter(Boolean).join(' ') : '';
  const utiliserControle = (c) => {
    setCarte(c); setControle(c.numero || ''); setCarteOk(true); setFenetreCarte(false); setAutreOutil(false); setErreur(null);
    if (c.identite?.matricule && !choix) setFiltre(String(c.identite.matricule));
  };
  const etape3 = etape2 && piece.prete;

  const recommencer = () => {
    setCarteOk(false); setControle(''); setCarte(null); setAutreOutil(false); setChoix(null); setQuantite(''); setBoucles(''); setErreur(null); setResultat(null); setCle(nouvelleCle());
    piece.setSaisie({ fichier: null, typePiece: 'BON_REMISE', libelle: '' });
    liste.recharger();
  };
  const confirmer = async () => {
    setEnCours(true); setErreur(null);
    try {
      const pc = await piece.deposer();
      const r = await api.remettre(campagne.id, {
        inscription_id: choix.id, quantite: q, piece_emargement_id: pc.id,
        ...(controle.trim() ? { carte_controle_id: controle.trim() } : {}), ...(listeBoucles.length ? { numeros_boucles: listeBoucles } : {}),
      }, cle);
      setResultat(r);
      if (onFaite) onFaite();
    } catch (e) {
      if (cleARenouveler(e)) setCle(nouvelleCle()); // refus 4xx éventuellement mémorisé : la remise corrigée part avec une nouvelle clé
      setErreur(e.normalisee || erreurSociale(e));
    }
    setEnCours(false);
  };

  if (resultat) return <ResultatRemise campagne={campagne} resultat={resultat} onNouvelle={recommencer} />;
  return (
    <section className="rh-carte">
      <TitreSection>Remettre au guichet</TitreSection>
      <ol style={{ margin: 0, padding: 0 }}>
        <Etape n={1} titre="Contrôler la carte QR du salarié" fait={etape1}>
          <p style={{ margin: '0 0 8px', fontSize: 14 }}>
            Le contrôle doit être fait par vous, être valide, dater de moins de {FRAICHEUR_MIN} minutes au moment de confirmer la remise et n'avoir jamais servi pour une autre remise.
          </p>
          {carte ? (
            <div className="rh-carte" style={{ margin: '0 0 8px', padding: '10px 12px', border: '2px solid var(--sg-succes)', background: 'var(--sg-succes-doux)' }} data-carte-controlee>
              <p style={{ margin: 0, fontSize: 14, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <CheckCircle2 size={16} color="var(--sg-succes)" aria-hidden="true" />
                <strong>Carte valide</strong> — {nomCarte || 'salarié'}{matriculeCarte ? ` (matricule ${matriculeCarte})` : ''}, contrôlée le {dateHeureLongue(carte.le)}.
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 14, overflowWrap: 'anywhere' }}>
                {carte.numero ? <>Numéro du contrôle : <code>{carte.numero}</code></> : 'Numéro du contrôle non communiqué : le serveur retiendra votre dernier contrôle valide de ce salarié.'}
              </p>
              {ageCarteMin > FRAICHEUR_MIN && <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--sg-alerte)' }}>Ce contrôle date de plus de {FRAICHEUR_MIN} minutes : refaites-le.</p>}
              <div style={{ marginTop: 8 }}><Bouton petit Icone={ScanQrCode} onClick={() => setFenetreCarte(true)}>Refaire le contrôle</Bouton></div>
            </div>
          ) : (
            <>
              <div style={{ margin: '0 0 8px' }}><Bouton variante="primaire" Icone={ScanQrCode} onClick={() => setFenetreCarte(true)}>Contrôler la carte</Bouton></div>
              {!autreOutil ? (
                <Bouton petit variante="lien" onClick={() => setAutreOutil(true)}>J'ai contrôlé la carte avec un autre outil</Bouton>
              ) : (
                <>
                  <Champ libelle="Numéro du contrôle de carte (facultatif)" aide={controleOk ? 'Identifiant affiché par l\'outil de contrôle. Sans numéro, le serveur retient votre dernier contrôle valide de ce salarié.' : 'Numéro de contrôle invalide.'}>
                    <input className="rh-champ-saisie" value={controle} onChange={(e) => setControle(e.target.value)} placeholder="Laisser vide : dernier contrôle" style={{ maxWidth: 420 }} />
                  </Champ>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14 }}>
                    <input type="checkbox" checked={carteOk} onChange={(e) => setCarteOk(e.target.checked)} style={{ marginTop: 3 }} />
                    <span>J'ai contrôlé la carte QR du salarié qui se présente ; le contrôle est valide.</span>
                  </label>
                </>
              )}
            </>
          )}
        </Etape>
        {etape1 && (
          <Etape n={2} titre="Choisir l'attribution et la quantité" fait={etape2}>
            <EtatsLecture lecture={liste} Icone={PackageCheck} estVide={!candidats.length} titreVide="Aucune unité à remettre"
              vide="Aucune inscription n'a d'unité attribuée restant à remettre : l'attribution doit être arrêtée.">
              {!choix ? (
                <>
                  <Champ libelle="Rechercher le salarié" aide="Nom, matricule ou numéro d'inscription.">
                    <div className="rh-recherche"><Search size={16} aria-hidden="true" /><input type="search" className="rh-champ-saisie" value={filtre} onChange={(e) => setFiltre(e.target.value)} /></div>
                  </Champ>
                  <ul role="listbox" aria-label="Attributions à remettre" style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--sg-bord)', borderRadius: 6, maxHeight: 260, overflowY: 'auto' }}>
                    {visibles.slice(0, 50).map((i) => (
                      <li key={i.id}>
                        <button type="button" role="option" aria-selected="false" className="rh-menu-item" style={{ width: '100%', textAlign: 'left', display: 'block', padding: '8px 10px' }}
                          onClick={() => { setChoix(i); setQuantite(String(reste(i))); setErreur(null); }}>
                          <strong style={{ fontWeight: 500 }}>{i.salarie}</strong> <span className="rh-cellule-secondaire">{i.matricule} · {i.numero}</span>
                          <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Attribué : {quantiteUnites(campagne, i.quantite_attribuee)} · déjà remis : {i.quantite_remise || 0} · à remettre : {reste(i)}</span>
                        </button>
                      </li>
                    ))}
                    {!visibles.length && <li className="rh-form-aide" style={{ padding: 10 }}>Aucune attribution ne correspond.</li>}
                  </ul>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 14, marginBottom: 10 }}>
                    <strong style={{ fontWeight: 500 }}>{choix.salarie}</strong><span className="rh-cellule-secondaire">{choix.matricule} · {choix.numero}</span>
                    <Bouton petit variante="lien" onClick={() => { setChoix(null); setErreur(null); }}>Changer</Bouton>
                  </div>
                  {carteAutre && (
                    <BandeauInfo ton="danger" titre="La carte contrôlée n'est pas celle de ce salarié">
                      Carte contrôlée : matricule {matriculeCarte}. Salarié choisi : matricule {choix.matricule}. Contrôlez la carte de la personne qui reçoit les unités, ou changez de salarié.
                    </BandeauInfo>
                  )}
                  <p style={{ margin: '0 0 10px', fontSize: 14 }}>Attribué : {quantiteUnites(campagne, choix.quantite_attribuee)} · déjà remis : {choix.quantite_remise || 0} · <strong>à remettre : {quantiteUnites(campagne, reste(choix))}</strong></p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                    <Champ libelle={`Quantité remise (${campagne.unite_pluriel})`} requis aide={reste(choix) > 1 ? 'Remise partielle possible : le reste sera remis plus tard.' : undefined}>
                      <input className="rh-champ-saisie" type="number" inputMode="numeric" min={1} max={reste(choix)} value={quantite} onChange={(e) => setQuantite(e.target.value)} />
                    </Champ>
                    <Champ libelle="Numéros d'identification (facultatifs)" aide={bouclesOk ? 'Un par unité au plus, séparés par des virgules.' : 'Plus de numéros que d\'unités remises.'}>
                      <input className="rh-champ-saisie" value={boucles} onChange={(e) => setBoucles(e.target.value)} />
                    </Champ>
                  </div>
                </>
              )}
            </EtatsLecture>
          </Etape>
        )}
        {etape2 && (
          <Etape n={3} titre="Pièce d'émargement" fait={etape3}>
            <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Bon de remise signé par le salarié (photo ou scan). La pièce est déposée une seule fois, même si la remise doit être reprise.</p>
            <ChampPiece piece={piece} types={['BON_REMISE']} libelleChamp="Bon de remise signé" />
          </Etape>
        )}
        {etape3 && (
          <Etape n={4} titre="Confirmer la remise" fait={false}>
            <p style={{ margin: '0 0 8px', fontSize: 14 }}>
              Remettre <strong>{quantiteUnites(campagne, q)}</strong> à <strong>{choix.salarie}</strong> au prix figé à l'inscription ({francs(choix.prix_unitaire)} par {campagne.unite_singulier}).
              Le serveur calcule l'aide (comptée une seule fois pour l'inscription), constitue la créance et son échéancier, puis propose les retenues à la Paie, qui restent bloquées.
            </p>
            <ErreurServeur erreur={erreur} />
            <Bouton variante="primaire" Icone={QrCode} disabled={enCours} onClick={confirmer}>Confirmer la remise</Bouton>
          </Etape>
        )}
      </ol>
      {fenetreCarte && <FenetreControleCarte onFermer={() => setFenetreCarte(false)} onUtiliser={utiliserControle} />}
    </section>
  );
}

function ListeRemises({ campagne, version }) {
  const api = useCampagnes();
  const l = useLectureSociale(() => api.remises(campagne.id), [api, campagne.id, version]);
  const remises = toArray(l.donnees);
  return (
    <section className="rh-carte" style={{ padding: 0 }}>
      <div style={{ padding: '12px 16px 0' }}><TitreSection>Remises effectuées</TitreSection></div>
      <EtatsLecture lecture={l} Icone={PackageCheck} estVide={!remises.length} titreVide="Aucune remise" vide="Chaque remise sort les unités du stock et constitue la créance du salarié (sauf reste nul).">
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Remise</th><th>Salarié</th><th>Quantité</th><th style={{ textAlign: 'right' }}>Brut</th><th style={{ textAlign: 'right' }}>Aide</th><th style={{ textAlign: 'right' }}>Constitué</th><th>Créance</th></tr></thead>
            <tbody>
              {remises.map((r) => (
                <tr key={r.id}>
                  <td>{r.numero}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{dateHeureLongue(r.le)}{r.statut === 'ANNULEE' ? ' · annulée' : ''}</span></td>
                  <td>{r.salarie}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{r.matricule} · {r.inscription_numero}</span></td>
                  <td>{quantiteUnites(campagne, r.quantite)}</td>
                  <td style={{ textAlign: 'right' }}><Montant n={r.montant_brut} /></td>
                  <td style={{ textAlign: 'right' }}><Montant n={r.aide_imputee} /></td>
                  <td style={{ textAlign: 'right' }}><Montant n={r.montant_constitue} fort /></td>
                  <td>{r.creance_id ? <Link className="rh-lien" to={`/dispositifs/${campagne.organisation_id}/tresorerie/creances/${r.creance_id}`}>Ouvrir</Link> : <span className="rh-cellule-secondaire">Aucune</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </EtatsLecture>
    </section>
  );
}

export default function RemisesVue({ campagne, droits }) {
  const [version, setVersion] = useState(0);
  const enDistribution = codeEtat(campagne) === 'EN_DISTRIBUTION';
  return (
    <>
      <BandeauRetenuesSimulation />
      {droits.a('ds_remise_effectuer') && enDistribution && <RemiseGuichet campagne={campagne} onFaite={() => setVersion((v) => v + 1)} />}
      {droits.a('ds_remise_effectuer') && !enDistribution && (
        <BandeauInfo ton="info">Les remises se font quand la campagne est en distribution (après l'arrêt de l'attribution et la réception des unités).</BandeauInfo>
      )}
      <ListeRemises campagne={campagne} version={version} />
    </>
  );
}
